// Tooltip Component - Reusable tooltip for items, abilities, and UI elements
// MapleStory-inspired dark box style

import React, { cloneElement, isValidElement, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COLORS, RARITY } from '../theme';
import type { CharacterClass } from '../../../shared/CharacterTypes';

export { createItemTooltip, compareWithWorn } from './itemTooltip';

export type TooltipRequirementStatus = 'met' | 'unmet' | 'unknown';

export interface TooltipRequirement {
  text: string;
  status: TooltipRequirementStatus;
}

/** What the viewer wears in one item slot, for the compare-to-worn delta. */
export interface WornItemForCompare {
  id: string;
  name?: string;
  stats?: Record<string, number>;
}

export interface ItemTooltipViewerContext {
  level?: number | null;
  characterClass?: CharacterClass | null;
  /** Worn item per item slot (`weapon`, `hat`, …); absent = unknown, so no compare block. */
  equipped?: Partial<Record<string, WornItemForCompare | null>>;
}

/** The tooltip's compare block: stat changes if the viewer wore this instead (plan 23 row 27). */
export interface ItemComparison {
  /** Name of the worn item compared against; null when the slot is empty. */
  against: string | null;
  /** This very item is what the viewer wears. */
  worn: boolean;
  deltas: { label: string; delta: number }[];
}

/** Value colour per ability stat key (plan 29 F2). */
const ABILITY_STAT_COLORS: Record<string, string> = {
  damage: COLORS.health, heal: '#69db7c', mana: COLORS.mana, cooldown: COLORS.warning, cast: COLORS.info,
};

export interface TooltipData {
  title: string;
  subtitle?: string; // equippable item's slot under the title ("Trail", "Head")
  binding?: string; // "Quest item — bound to you" (plan 23 row 42)
  comparison?: ItemComparison;
  accentColor?: string;
  description?: string;
  rarity?: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  stats?: { label: string; value: string | number }[];
  /** Plan 29 F2: an ability's stat line, computed from content (abilityText.ts). */
  abilityStats?: { key: string; label: string; value: string }[];
  effects?: string[];
  effectLabel?: string;
  requirements?: TooltipRequirement[];
  cooldown?: number;
  /** Wind-up / cast time in ms — shown as "Cast: 0.8s" when > 0 (DJ 2026-06-12). */
  castTime?: number;
  manaCost?: number;
  damage?: number;
  range?: number;
  extraInfo?: string;
  interactionHint?: string;
}

interface TooltipProps {
  data: TooltipData | null;
  children: React.ReactNode;
  disabled?: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Wrap in a span so the trigger can sit inside a line of text (NPC item chips). */
  inline?: boolean;
}

const DELTA_UP = '#69db7c';
const DELTA_DOWN = '#ff6b6b';

const RARITY_COLORS: Record<string, string> = RARITY;
const REQUIREMENT_COLORS: Record<TooltipRequirementStatus, string> = {
  met: '#69db7c',
  unmet: '#ff6b6b',
  unknown: COLORS.textSecondary,
};

export const Tooltip: React.FC<TooltipProps> = ({
  data,
  children,
  disabled = false,
  position = 'top',
  inline = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  // The pointer the tooltip follows; null when it is anchored to a focused element.
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  // The first placement guesses the height; once drawn, sit the real box
  // above the pointer so a tall item tooltip never covers what is hovered.
  useLayoutEffect(() => {
    const box = tooltipRef.current;
    const pointer = pointerRef.current;
    if (!isVisible || !box || !pointer || box.offsetHeight <= 0) return;
    const padding = 10;
    let y = pointer.y - box.offsetHeight - padding;
    if (y < 0) y = Math.min(pointer.y + padding, window.innerHeight - box.offsetHeight - padding);
    if (Math.abs(y - tooltipPosition.y) > 1) setTooltipPosition(position => ({ ...position, y }));
  }, [isVisible, tooltipPosition.y]);

  // Use pointer events — they fire more reliably on draggable elements
  const handlePointerEnter = (e: React.PointerEvent) => {
    if (disabled || !data) return;
    setIsVisible(true);
    updatePosition(e);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (disabled || !data) return;
    updatePosition(e);
  };

  const handlePointerLeave = () => {
    setIsVisible(false);
  };

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (disabled || !data) return;
    setIsVisible(true);
    updatePositionFromRect(e.currentTarget.getBoundingClientRect());
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    setIsVisible(false);
  };

  const updatePosition = (e: React.PointerEvent | React.MouseEvent) => {
    const tooltipWidth = 220;
    const tooltipHeight = 230;
    const padding = 10;

    const clientX = Number.isFinite(e.clientX) ? e.clientX : padding;
    const clientY = Number.isFinite(e.clientY) ? e.clientY : tooltipHeight + padding;
    pointerRef.current = { x: clientX, y: clientY };
    let x = clientX + padding;
    let y = clientY - tooltipHeight - padding;

    // Keep tooltip on screen
    if (x + tooltipWidth > window.innerWidth) {
      x = clientX - tooltipWidth - padding;
    }
    if (y < 0) {
      y = clientY + padding;
    }

    setTooltipPosition({ x, y });
  };

  const updatePositionFromRect = (rect: DOMRect) => {
    pointerRef.current = null;
    const tooltipWidth = 220;
    const tooltipHeight = 230;
    const padding = 10;
    let x = rect.left + (rect.width - tooltipWidth) / 2;
    let y = rect.top - tooltipHeight - padding;

    if (position === 'bottom') y = rect.bottom + padding;
    if (position === 'left') {
      x = rect.left - tooltipWidth - padding;
      y = rect.top;
    }
    if (position === 'right') {
      x = rect.right + padding;
      y = rect.top;
    }

    x = Math.max(padding, Math.min(x, window.innerWidth - tooltipWidth - padding));
    y = Math.max(padding, Math.min(y, window.innerHeight - tooltipHeight - padding));
    setTooltipPosition({ x, y });
  };

  if (!data) {
    return <>{children}</>;
  }

  const titleColor = data.accentColor ?? (data.rarity ? RARITY_COLORS[data.rarity] : '#ffffff');
  const describedChild = isValidElement(children)
    ? cloneElement(children as React.ReactElement<{ 'aria-describedby'?: string }>, {
      'aria-describedby': tooltipId,
    })
    : children;

  const Wrapper = inline ? 'span' : 'div';
  return (
    <Wrapper
      ref={containerRef as React.RefObject<HTMLDivElement & HTMLSpanElement>}
      onPointerEnter={handlePointerEnter}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{ position: 'relative', ...(inline ? { display: 'inline' } : {}) }}
    >
      {describedChild}

      {isVisible && createPortal(
        <div
          id={tooltipId}
          role="tooltip"
          ref={tooltipRef}
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            zIndex: 10000,
            pointerEvents: 'none',
            minWidth: '180px',
            maxWidth: '280px',
            background: 'rgba(0, 0, 0, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            padding: '10px 12px',
            color: '#ffffff',
            fontFamily: '\'Inter\', sans-serif',
            fontSize: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Title */}
          <div style={{
            fontWeight: 'bold',
            fontSize: '14px',
            color: titleColor,
            marginBottom: data.description || data.effects?.length || data.requirements?.length ? '6px' : '0',
            textTransform: 'capitalize'
          }}>
            {data.title}
          </div>
          {data.subtitle && <div data-tooltip-subtitle style={{ color: COLORS.textMuted, fontSize: '11px', marginTop: '-4px', marginBottom: '6px' }}>{data.subtitle}</div>}
          {data.binding && <div data-tooltip-binding style={{ color: '#ffd43b', fontSize: '11px', marginBottom: '6px' }}>{data.binding}</div>}

          {/* Description */}
          {data.description && (
            <div style={{
              color: '#aaaaaa',
              fontSize: '11px',
              lineHeight: '1.4',
              marginBottom: '8px'
            }}>
              {data.description}
            </div>
          )}

          {/* Item use effects */}
          {data.effects && data.effects.length > 0 && (
            <div style={{
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '6px',
              marginTop: '4px',
            }}>
              {data.effects.map((effect, index) => (
                <div key={index} style={{ color: '#69db7c', lineHeight: '1.4', marginBottom: '2px' }}>
                  <span style={{ color: '#ffd43b' }}>{data.effectLabel ?? 'Use'}: </span>
                  {effect}
                </div>
              ))}
            </div>
          )}

          {/* Requirements */}
          {data.requirements && data.requirements.length > 0 && (
            <div style={{
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '6px',
              marginTop: '4px',
            }}>
              {data.requirements.map((requirement, index) => (
                <div
                  key={index}
                  data-requirement-status={requirement.status}
                  style={{ color: REQUIREMENT_COLORS[requirement.status], lineHeight: '1.4' }}
                >
                  {requirement.text}
                </div>
              ))}
            </div>
          )}

          {/* Combat Stats */}
          {(data.damage || data.manaCost || data.cooldown || data.castTime || data.range) && (
            <div style={{
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '6px',
              marginTop: '4px'
            }}>
              {data.damage && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: COLORS.textMuted }}>Damage:</span>
                  <span style={{ color: COLORS.health }}>{data.damage}</span>
                </div>
              )}
              {data.manaCost && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: COLORS.textMuted }}>Mana:</span>
                  <span style={{ color: COLORS.mana }}>{data.manaCost}</span>
                </div>
              )}
              {data.castTime ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: COLORS.textMuted }}>Cast:</span>
                  <span style={{ color: COLORS.info }}>{(data.castTime / 1000).toFixed(1)}s</span>
                </div>
              ) : null}
              {data.cooldown && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: COLORS.textMuted }}>Cooldown:</span>
                  <span style={{ color: COLORS.warning }}>{(data.cooldown / 1000).toFixed(1)}s</span>
                </div>
              )}
              {data.range && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: COLORS.textMuted }}>Range:</span>
                  <span>{data.range}</span>
                </div>
              )}
            </div>
          )}

          {/* Ability stat line (plan 29 F2) — computed from content */}
          {data.abilityStats && data.abilityStats.length > 0 && (
            <div data-tooltip-ability-stats style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', marginTop: '4px' }}>
              {data.abilityStats.map((stat) => (
                <div key={stat.key} data-ability-stat={stat.key}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '2px' }}>
                  <span style={{ color: COLORS.textMuted }}>{stat.label}</span>
                  <span style={{ color: ABILITY_STAT_COLORS[stat.key] ?? COLORS.textPrimary }}>{stat.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Custom Stats */}
          {data.stats && data.stats.length > 0 && (
            <div style={{
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '6px',
              marginTop: '4px'
            }}>
              {data.stats.map((stat, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ color: '#888' }}>{stat.label}:</span>
                  <span style={{ color: '#69db7c' }}>+{stat.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Compare with what the viewer wears in this slot (plan 23 row 27) */}
          {data.comparison && (
            <div data-tooltip-compare style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', marginTop: '4px' }}>
              <div style={{ color: COLORS.textMuted, fontSize: '11px', marginBottom: '3px' }}>
                {data.comparison.worn ? 'Currently worn'
                  : data.comparison.against ? `Compared with ${data.comparison.against}` : 'Nothing worn in this slot'}
              </div>
              {data.comparison.deltas.map(({ label, delta }) => (
                <div key={label} data-tooltip-delta={delta > 0 ? 'up' : 'down'}
                  style={{ display: 'flex', justifyContent: 'space-between', color: delta > 0 ? DELTA_UP : DELTA_DOWN }}>
                  <span>{label}</span><span>{delta > 0 ? '+' : '\u2212'}{Math.abs(delta)}</span>
                </div>
              ))}
              {!data.comparison.worn && data.comparison.deltas.length === 0 && (
                <div style={{ color: COLORS.textMuted }}>No stat change</div>
              )}
            </div>
          )}

          {/* Extra Info */}
          {data.extraInfo && (
            <div style={{
              color: '#ffd43b',
              fontSize: '10px',
              fontStyle: 'italic',
              marginTop: '6px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '6px'
            }}>
              {data.extraInfo}
            </div>
          )}

          {/* Discoverable mouse/drag contract for interactive items. */}
          {data.interactionHint && (
            <div style={{
              color: '#74c0fc',
              fontSize: '10px',
              marginTop: '6px',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: '6px'
            }}>
              {data.interactionHint}
            </div>
          )}
        </div>,
        document.body
      )}
    </Wrapper>
  );
};

// Helper to create tooltip data from an ability
export function createAbilityTooltip(ability: {
  name: string;
  description?: string;
  damage?: number;
  manaCost?: number;
  cooldown?: number;
  range?: number;
}): TooltipData {
  return {
    title: ability.name,
    description: ability.description,
    damage: ability.damage,
    manaCost: ability.manaCost,
    cooldown: ability.cooldown,
    range: ability.range
  };
}

export default Tooltip;
